// src/components/draft/DraftChat.js - Live Draft Chat Component
import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';

const DraftChat = ({ socket, userTeam }) => {
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Scroll to bottom of chat when new message arrives
  // Use block:'nearest' to only scroll the chat container, not the entire page
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Listen for chat messages
  useEffect(() => {
    if (!socket) return;
    
    const handleMessage = (message) => {
      setMessages(prev => [...prev, message]);
    };
    
    socket.on('draft-message', handleMessage);
    
    // Load existing chat history when draft state is received
    const handleDraftState = (state) => {
      if (state && state.chat && Array.isArray(state.chat)) {
        setMessages(state.chat);
      }
    };
    
    socket.on('draft-state', handleDraftState);
    
    return () => {
      socket.off('draft-message', handleMessage);
      socket.off('draft-state', handleDraftState);
    };
  }, [socket]);
  
  // Send message
  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!messageInput.trim() || sending || !socket) return;
    
    setSending(true);
    
    try {
      socket.emit('draft-message', {
        content: messageInput.trim()
      });
      
      setMessageInput('');
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };
  
  return (
    <div className={`${dynastyTheme.components.card.base} p-4 h-96 flex flex-col`}>
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
        <h3 className={dynastyTheme.components.heading.h4}>
          Draft Chat
        </h3>
      </div>
      
      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto mb-3 space-y-2 pr-2"
        style={{ maxHeight: 'calc(24rem - 120px)' }}
      >
        {messages.length === 0 ? (
          <div className={`text-center py-8 ${dynastyTheme.classes.text.neutralLight}`}>
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div 
              key={msg.message_id || `msg-${index}`}
              className={`p-2 rounded ${
                msg.type === 'system' 
                  ? `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight} text-sm italic border-l-2 ${dynastyTheme.classes.border.primary}`
                  : msg.team_name === userTeam?.team_name
                    ? `${dynastyTheme.classes.bg.primary} bg-opacity-10 border-l-2 ${dynastyTheme.classes.border.primaryBright}`
                    : `${dynastyTheme.classes.bg.darkLighter} border-l-2 ${dynastyTheme.classes.border.neutral}`
              }`}
            >
              {msg.type === 'system' ? (
                <div className="text-center">
                  {msg.content}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`${dynastyTheme.classes.text.primary} font-semibold text-sm`}>
                      {msg.team_name || 'Unknown Team'}
                    </span>
                    <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                  <div className={dynastyTheme.classes.text.white}>
                    {msg.content}
                  </div>
                </>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <form onSubmit={handleSendMessage} className="flex gap-2">
        <input
          type="text"
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          placeholder="Type a message..."
          maxLength={500}
          disabled={sending || !socket}
          className={`${dynastyTheme.components.input} flex-1`}
        />
        <button
          type="submit"
          disabled={sending || !messageInput.trim() || !socket}
          className={`${dynastyTheme.components.button.primary} flex items-center gap-2`}
        >
          <Send className="w-4 h-4" />
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
      
      {/* Character count */}
      <div className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1 text-right`}>
        {messageInput.length} / 500
      </div>
    </div>
  );
};

export default DraftChat;

